import type { CalculatorResults, CalculatorState, SavingsPoint } from './types'

export const DEFAULT_STATE: CalculatorState = {
  age: 35,
  retirementAge: 60,
  income: 200_000,
  savings: 1_000_000,
  monthlyContribution: 2_500,
  budgetPct: 70,
  otherIncome: 1_500,
  lifeExpectancy: 95,
  preReturnRate: 0.08,
  postReturnRate: 0.05,
  inflationRate: 0.02,
  raiseRate: 0.03,
}

export function accumulate(pv: number, annualContrib: number, rate: number, raiseRate: number, years: number): number {
  let balance = pv
  let contrib = annualContrib
  for (let y = 0; y < years; y++) {
    balance = balance * (1 + rate) + contrib
    contrib *= 1 + raiseRate
  }
  return balance
}

/** Year-by-year balances behind `accumulate`, one point per age from today through retirement. */
export function projectSavingsByAge(state: CalculatorState): SavingsPoint[] {
  const retirementAge = Math.max(state.retirementAge, state.age + 1)
  const points: SavingsPoint[] = [{ age: state.age, savings: state.savings }]
  for (let years = 1; years <= retirementAge - state.age; years++) {
    points.push({
      age: state.age + years,
      savings: accumulate(state.savings, state.monthlyContribution * 12, state.preReturnRate, state.raiseRate, years),
    })
  }
  return points
}

export function pvAnnuity(annualWithdrawal: number, returnRate: number, inflationRate: number, years: number): number {
  const realRate = (1 + returnRate) / (1 + inflationRate) - 1
  if (Math.abs(realRate) < 0.0001) return annualWithdrawal * years
  return (annualWithdrawal * (1 - Math.pow(1 + realRate, -years))) / realRate
}

export function computeResults(state: CalculatorState): CalculatorResults {
  const {
    age,
    retirementAge,
    income,
    savings,
    monthlyContribution,
    budgetPct,
    otherIncome,
    lifeExpectancy,
    preReturnRate,
    postReturnRate,
    inflationRate,
    raiseRate,
  } = state

  const safeRetAge = Math.max(retirementAge, age + 1)
  const safeLifeExp = Math.max(lifeExpectancy, safeRetAge + 1)
  const yrsToRet = safeRetAge - age
  const yrsInRet = safeLifeExp - safeRetAge

  const projectedSalary = income * Math.pow(1 + raiseRate, yrsToRet)
  const monthlyBudget = (projectedSalary * (budgetPct / 100)) / 12
  const otherAtRet = otherIncome * Math.pow(1 + inflationRate, yrsToRet)
  const monthlyGapAtRet = Math.max(monthlyBudget - otherAtRet, 0)

  const whatYouHave = accumulate(savings, monthlyContribution * 12, preReturnRate, raiseRate, yrsToRet)
  const whatYouNeed = pvAnnuity(monthlyGapAtRet * 12, postReturnRate, inflationRate, yrsInRet)

  const ratio = whatYouNeed > 0 ? whatYouHave / whatYouNeed : whatYouHave > 0 ? 2 : 0
  const gap = whatYouHave - whatYouNeed

  return {
    projectedSalary,
    monthlyBudget,
    otherAtRet,
    monthlyGapAtRet,
    whatYouHave,
    whatYouNeed,
    ratio,
    gap,
    yrsToRet,
    yrsInRet,
  }
}
